import React from 'react'

export interface ValidationFieldError {
  field: string
  errors: string[]
}

/**
 * Convert a camelCase field name to a human-readable label.
 * `carrierApplicationHixmeSubmittedBy` → `Carrier Application Hixme Submitted By`
 */
export function formatFieldName(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}

/**
 * class-validator prefixes each error message with the property name.
 * Strip that prefix to avoid duplication when the field name is already shown as a label.
 * `carrierApplicationHixmeSubmittedBy must be a string` → `must be a string`
 */
function stripFieldPrefix(field: string, message: string): string {
  const prefix = `${field} `
  return message.startsWith(prefix) ? message.slice(prefix.length) : message
}

/**
 * Parse class-validator error responses into a structured array.
 * Returns `null` when the error is not a validation error.
 *
 * Backend 422 shape — a flat (non-nested) DTO produces field/error pairs
 * directly at the top level:
 * ```json
 * { "message": [{ "field": "someField", "errors": ["must be a string"] }] }
 * ```
 * A DTO with a `@ValidateNested()` property adds one wrapping level per
 * such property:
 * ```json
 * {
 *   "message": [
 *     {
 *       "field": "data",
 *       "errors": [
 *         { "field": "someField", "errors": ["must be a string"] }
 *       ]
 *     }
 *   ]
 * }
 * ```
 *
 * Error messages have the field name prefix stripped automatically.
 */
export function parseApiValidationErrors(
  error: any
): ValidationFieldError[] | null {
  const raw = error?.response?.data?.message
  if (!Array.isArray(raw)) return null

  try {
    const result: ValidationFieldError[] = []

    for (const group of raw) {
      if (!Array.isArray(group.errors)) continue

      // Flat (zero-level): { field: "name", errors: ["must be a string"] }
      // directly at the top level — a flat (non-nested) DTO produces this
      // shape with no wrapping property around it.
      if (typeof group.errors[0] === 'string') {
        result.push({
          field: group.field,
          errors: group.errors.map((e: string) =>
            stripFieldPrefix(group.field, e)
          ),
        })
        continue
      }

      for (const row of group.errors) {
        // Single-level: { field: "name", errors: ["must be a string"] }
        if (Array.isArray(row.errors) && typeof row.errors[0] === 'string') {
          result.push({
            field: row.field,
            errors: row.errors.map((e: string) =>
              stripFieldPrefix(row.field, e)
            ),
          })
          continue
        }

        // Two-level (row-based): { field: "0", errors: [{ field: "name", errors: [...] }] }
        if (Array.isArray(row.errors)) {
          for (const nested of row.errors) {
            if (Array.isArray(nested.errors)) {
              result.push({
                field: nested.field,
                errors: nested.errors.map((e: string) =>
                  stripFieldPrefix(nested.field, e)
                ),
              })
            }
          }
        }
      }
    }

    return result.length > 0 ? result : null
  } catch {
    return null
  }
}

/**
 * Renders a compact list of validation field errors for use in notifications or alerts.
 * Each row: **Field Name**: error 1, error 2
 */
export function ValidationErrorList({
  errors,
}: {
  errors: ValidationFieldError[]
}) {
  return (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {errors.map(({ field, errors: fieldErrors }) => (
        <li key={field}>
          <strong>{formatFieldName(field)}</strong>: {fieldErrors.join(', ')}
        </li>
      ))}
    </ul>
  )
}

/**
 * Extract a single-string error message from an Axios error.
 * Handles plain strings, NestJS field-map messages, and class-validator arrays.
 * Use `parseApiValidationErrors` when you need the structured array instead.
 */
export function extractApiErrorMessage(
  error: any,
  fallback = 'An unexpected error occurred.'
): string {
  const data = error?.response?.data

  if (typeof data === 'string' && data.trim()) return data.trim()

  if (data && typeof data === 'object') {
    const raw = data.message

    if (typeof raw === 'string' && raw.trim()) return raw.trim()

    // NestJS validation pipe can return message as a field map instead of a string
    // or class-validator array, e.g. { personPublicKey: ["must be a UUID"] }.
    // Flatten so notifications show readable text instead of "[object Object]".
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const parts = Object.entries(raw as Record<string, unknown>).flatMap(
        ([field, value]) => {
          const messages = Array.isArray(value) ? value : [value]
          return messages
            .filter((m): m is string => typeof m === 'string')
            .map((m) => `${formatFieldName(field)}: ${m}`)
        }
      )
      if (parts.length > 0) return parts.join('\n')
    }

    const validationErrors = parseApiValidationErrors(error)
    if (validationErrors) {
      return validationErrors
        .map((e) => `${formatFieldName(e.field)}: ${e.errors.join(', ')}`)
        .join('\n')
    }

    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim()
    }
  }

  const status = error?.response?.status
  const axiosMessage =
    typeof error?.message === 'string' ? error.message.trim() : ''

  if (
    axiosMessage &&
    !/^Request failed with status code \d+$/.test(axiosMessage)
  ) {
    return axiosMessage
  }

  if (typeof status === 'number') {
    if (status >= 500) {
      return `Server error (${status}). Please try again later.`
    }
    if (status >= 400) {
      return `Request failed (${status}). Please try again.`
    }
  }

  if (axiosMessage) return axiosMessage

  return fallback
}

/**
 * Build props for `notification.error()` from an Axios error.
 *
 * - Validation errors → `{ message: 'Validation Error', description: <structured list> }`
 * - Plain string / other → `{ message: 'the error text' }`
 */
export function apiErrorToNotificationProps(
  error: any,
  fallback = 'An unexpected error occurred.'
): { message: string; description?: React.ReactNode } {
  const validationErrors = parseApiValidationErrors(error)

  if (validationErrors) {
    return {
      message: 'Validation Error',
      description: <ValidationErrorList errors={validationErrors} />,
    }
  }

  return { message: extractApiErrorMessage(error, fallback) }
}
